/**
 * Markup parser — converts HTML strings to ContentUnit (raw-only).
 *
 * Every inline element becomes an indexed tag whose style is the verbatim
 * opening tag in canonical form (see canonical-tag.ts). No element-to-
 * semantic-style mapping — wrappers round-trip exactly.
 *
 * @module content/parser/MarkupParser
 */

import type { IParser } from './IParser.js';
import type { ContentUnit } from '../types.js';
import { serializeOpeningTag, VOID_TAGS } from '../canonical-tag.js';

interface TagMatch {
  tag: string;
  attrs: Record<string, string>;
  isClosing: boolean;
  isSelfClosing: boolean;
  startIndex: number;
  endIndex: number;
}

/**
 * Parser that converts HTML markup strings to ContentUnit (raw-only).
 *
 * @example
 * ```typescript
 * const parser = new MarkupParser();
 * parser.parse('Read our <b>Terms</b>.');
 * // → { text: 'Read our <1>Terms</1>.', styles: { '1': ['raw:<b>'] } }
 * ```
 */
/**
 * Decode named + numeric HTML entities to their literal Unicode characters.
 *
 * Why: the parser may be fed strings from several sources that historically
 * encoded entities differently — developer-authored JSX strings (`<Trans
 * defaultValue="It&apos;s working">`), cached translation values that came
 * through `tHtml`, or server-rendered HTML processed by edge/WP. The other
 * three parsers in the stack (browser DOM, edge regex, PHP regex) all see
 * decoded characters by the time they hash. Decoding here so this parser
 * does too keeps `auto_` keys consistent across every entry point. Decode
 * is a no-op for entity-free input, which is the overwhelmingly common
 * case in JSX where developers type literal characters.
 *
 * Covers the named entities WordPress's wptexturize commonly emits plus
 * all numeric forms (`&#NNN;` / `&#xHHH;`). Anything else is left alone.
 */
function decodeEntities(text: string): string {
  const named: Record<string, string> = {
    amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
    rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“',
    hellip: '…', mdash: '—', ndash: '–', copy: '©',
    reg: '®', trade: '™',
  };
  return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (m, body) => {
    if (body[0] === '#') {
      const cp = body[1] === 'x' || body[1] === 'X'
        ? parseInt(body.slice(2), 16)
        : parseInt(body.slice(1), 10);
      if (Number.isFinite(cp) && cp > 0) {
        try { return String.fromCodePoint(cp); } catch { return m; }
      }
      return m;
    }
    return named[body.toLowerCase()] ?? m;
  });
}

export class MarkupParser implements IParser<string> {
  parse(input: string): ContentUnit {
    // Already indexed tags from server — still decode entities so the text
    // hashes identically regardless of which path produced it.
    if (/<\/?[0-9]+>/.test(input)) {
      return { text: decodeEntities(input), styles: {} };
    }

    const styles: Record<string, string[]> = {};
    let tagIndex = 0;
    const tagStack: Array<{ tag: string; index: number }> = [];
    let result = '';
    let pos = 0;

    while (pos < input.length) {
      const tag = this.matchTag(input, pos);
      if (!tag) {
        result += input.slice(pos);
        break;
      }

      result += input.slice(pos, tag.startIndex);

      if (tag.isClosing) {
        let matchIdx: number | null = null;
        for (let i = tagStack.length - 1; i >= 0; i--) {
          if (tagStack[i].tag === tag.tag.toLowerCase()) {
            matchIdx = tagStack[i].index;
            tagStack.splice(i, 1);
            break;
          }
        }
        if (matchIdx !== null) {
          result += `</${matchIdx}>`;
        }
        // Stray close — drop.
      } else {
        const tagName = tag.tag.toLowerCase();
        const isVoid = VOID_TAGS.has(tagName);
        const isSelfClosing = tag.isSelfClosing || isVoid;

        tagIndex++;
        styles[String(tagIndex)] = [`raw:${serializeOpeningTag(tagName, tag.attrs)}`];

        if (isSelfClosing) {
          result += `<${tagIndex}/>`;
        } else {
          result += `<${tagIndex}>`;
          tagStack.push({ tag: tagName, index: tagIndex });
        }
      }

      pos = tag.endIndex;
    }

    while (tagStack.length > 0) {
      const unclosed = tagStack.pop()!;
      result += `</${unclosed.index}>`;
    }

    return { text: decodeEntities(result), styles };
  }

  private matchTag(input: string, startPos: number): TagMatch | null {
    const tagStart = input.indexOf('<', startPos);
    if (tagStart === -1) return null;

    const tagPattern =
      /^<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s+[a-zA-Z][a-zA-Z0-9-]*(?:=(?:"[^"]*"|'[^']*'|[^\s>]*))?)*)\s*(\/?)>/;
    const remaining = input.slice(tagStart);
    const match = remaining.match(tagPattern);

    if (!match) {
      if (tagStart + 1 < input.length) {
        return this.matchTag(input, tagStart + 1);
      }
      return null;
    }

    const [fullMatch, closingSlash, tagName, attrsString, selfClosingSlash] = match;
    return {
      tag: tagName,
      attrs: this.parseAttributes(attrsString),
      isClosing: closingSlash === '/',
      isSelfClosing: selfClosingSlash === '/',
      startIndex: tagStart,
      endIndex: tagStart + fullMatch.length,
    };
  }

  private parseAttributes(attrsString: string): Record<string, string> {
    const attrs: Record<string, string> = {};
    if (!attrsString.trim()) return attrs;

    const attrPattern = /([a-zA-Z][a-zA-Z0-9-]*)(?:=(?:"([^"]*)"|'([^']*)'|([^\s>]*)))?/g;
    let match;
    while ((match = attrPattern.exec(attrsString)) !== null) {
      const [, name, doubleQuoted, singleQuoted, unquoted] = match;
      attrs[name.toLowerCase()] = doubleQuoted ?? singleQuoted ?? unquoted ?? '';
    }
    return attrs;
  }
}
